/**
 * Credit Limit Banner Hook
 *
 * Shared hook for checking credit usage and determining if credit limit banner should be displayed
 * 
 * @package @ideoz/core
 */

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/slices/appSlice';
import { isWebBuild } from '../../../utils/environment';

export interface CreditLimitBannerState {
  /**
   * Whether banner should be visible (75-100% credit usage)
   */
  isVisible: boolean;
  
  /**
   * Current plan name (e.g., "Free", "Smart")
   */
  planName: string;
  
  /**
   * Used credits this month
   */
  usedCredits: number;
  
  /**
   * Total monthly credits
   */
  totalCredits: number;
  
  /**
   * Credit usage percentage (0-100)
   */
  usagePercentage: number;
  
  /**
   * Whether user is at 100% usage
   */
  isAt100Percent: boolean;
  
  /**
   * Banner text message
   */
  bannerText: string;
  
  /**
   * Whether user is on Free plan (to show Upgrade button)
   */
  isFreePlan: boolean;
  
  /**
   * Function to set banner visibility
   * Hides the banner until page refresh
   */
  setVisible: (visible: boolean) => void;
}

/**
 * Hook to check credit limit and determine banner visibility
 * 
 * Note: Currently uses a flag in user.metadata for credit data.
 * This will be updated in the future when API provides actual credit data.
 * Plan information is read from user.planDetails.
 * 
 * @returns Credit limit banner state with setVisible function
 */
const STORAGE_KEY = 'ideoz-credit-limit-banner-hidden';
const SESSION_ID_KEY = 'ideoz-credit-limit-banner-session-id';

// Generate a unique session ID for this page load (changes on refresh)
const getSessionId = (): string => {
  return `session-${Date.now()}-${Math.random()}`;
};

// Module-level variable to store current session ID (generated once per page load)
let currentSessionId: string | null = null;

// Get or create session ID for current page load
// This generates a NEW ID on each page load (including refresh)
const getCurrentSessionId = (): string => {
  // If we already have a session ID for this page load, return it
  if (currentSessionId) {
    return currentSessionId;
  }
  
  // Generate a new session ID for this page load
  currentSessionId = getSessionId();
  
  if (!isWebBuild() || typeof window === 'undefined') {
    return currentSessionId;
  }
  
  try {
    // Store it in sessionStorage so we can check if dismissals are from this session
    window.sessionStorage.setItem(SESSION_ID_KEY, currentSessionId);
  } catch (e) {
    console.warn('Failed to store session ID:', e);
  }
  
  return currentSessionId;
};

export function useCreditLimitBanner(): CreditLimitBannerState {
  const user = useAppSelector(selectUser);
  // Read planDetails directly from Redux state (not from user object)
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  
  // Get current session ID (changes on refresh)
  const currentSessionId = getCurrentSessionId();
  
  // Initialize state from sessionStorage (only if dismissed in current session)
  const getInitialHiddenState = (): boolean => {
    if (!isWebBuild()) return false;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        const storedSessionId = window.sessionStorage.getItem(SESSION_ID_KEY);
        
        // Only hide if dismissed in the current session (same session ID)
        if (stored === 'true' && storedSessionId === currentSessionId) {
          return true;
        }
      }
    } catch (e) {
      console.warn('Failed to read from sessionStorage:', e);
    }
    return false;
  };

  const [isManuallyHidden, setIsManuallyHidden] = useState(getInitialHiddenState);

  // Function to set banner visibility (hides until page refresh)
  const setVisible = useCallback((visible: boolean) => {
    const hidden = !visible;
    setIsManuallyHidden(hidden);
    
    // Persist to sessionStorage with current session ID (clears on page refresh)
    if (isWebBuild()) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          if (hidden) {
            window.sessionStorage.setItem(STORAGE_KEY, 'true');
            // Store current session ID so we know it was dismissed in this session
            window.sessionStorage.setItem(SESSION_ID_KEY, currentSessionId);
          } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (e) {
        console.warn('Failed to write to sessionStorage:', e);
      }
    }
  }, [currentSessionId]);

  const bannerData = useMemo(() => {
    // Default values
    let usedCredits = 0;
    let totalCredits = 0;
    let planName = 'Free';
    let isFreePlan = true;

    // Read plan details from Redux state (state.app.planDetails)
    if (planDetails?.name) {
      planName = planDetails.name;
      isFreePlan = planName === 'Free';
    }

    // Read credit data from planDetails.credits array
    // Credits structure: [{ type: "base", balance: 8, limit: 8 }, { type: "prototype", balance: 1, limit: 1 }]
    // Note: balance = remaining credits, limit = total monthly credits
    // Used credits = limit - balance
    if (planDetails?.credits && Array.isArray(planDetails.credits)) {
      // Sum up all credit types (base + prototype + any others)
      let totalLimit = 0;
      let totalBalance = 0;
      
      planDetails.credits.forEach((credit: any) => {
        if(credit.type !== 'base') {
          return;
        }
        // Handle both string and number types (API may return strings)
        const limit = typeof credit.limit === 'string' ? parseInt(credit.limit, 10) : (credit.limit || 0);
        const balance = typeof credit.balance === 'string' ? parseInt(credit.balance, 10) : (credit.balance || 0);
        totalLimit += limit;
        totalBalance += balance;
      });
      
      totalCredits = totalLimit;
      // balance = remaining credits, so used = limit - balance
      usedCredits = Math.max(0, totalLimit - totalBalance);
    }

    // Calculate usage percentage
    const usagePercentage = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
    
    // Banner should show when usage is 80-100%
    const shouldShow = usagePercentage >= 80 && usagePercentage <= 100;
    
    // Determine if at 100%
    const isAt100Percent = usagePercentage >= 100;
    
    // Generate banner text with plan and credit information
    let bannerText = '';
    if (totalCredits > 0) {
      if (isAt100Percent) {
        bannerText = `You finished your credits for this month`;
      } else {
        bannerText = `You've used ${usedCredits} out of ${totalCredits} credits this month (${usagePercentage.toFixed(0)}%)`;
      }
    } else {
      // Fallback if no credits data available
      bannerText = `Credit information is not available`;
    }

    return {
      planName,
      usedCredits,
      totalCredits,
      usagePercentage,
      isAt100Percent,
      bannerText,
      isFreePlan,
      // Banner is visible if conditions are met AND not manually hidden
      isVisible: shouldShow && !isManuallyHidden,
      setVisible,
    };
  }, [user, isManuallyHidden, setVisible]);

  return bannerData;
}
